import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { avatarFor } from "@/data/demo";
import { userAvatar, pfp } from "@/lib/avatar";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { HotBadge, LivePulse, SocialProof, Countdown, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

const CATS = ["All", "Polls", "Business Growth", "Client Acquisition", "Digital Marketing", "Compliance", "HNI Business", "Practice Management", "Market Discussion", "AI"];
const CAT_OPTIONS = ["Business Growth", "Client Acquisition", "Digital Marketing", "Compliance", "HNI Business", "Practice Management", "Market Discussion", "AI"];
const PROMPTS = [
  "What's working for you in client acquisition this quarter?",
  "Share a win: your best SIP recovery story",
  "How do you decode the latest SEBI/AMFI circular?",
  "Your favourite tool for managing 500+ clients?",
];
const LEADERBOARD = [
  { n: "Meera Kulkarni", pts: 4820, badge: "Community Expert" },
  { n: "Anjali Verma", pts: 4210, badge: "Compliance Champion" },
  { n: "Priya Nair", pts: 3890, badge: "Top Contributor" },
  { n: "Rohit Deshmukh", pts: 3510, badge: "Digital MFD" },
];

const activityLabel = (days) => {
  if (days == null) return { text: "New", tone: "text-emerald-600 bg-emerald-50" };
  if (days === 0) return { text: "Active today", tone: "text-emerald-600 bg-emerald-50" };
  if (days <= 3) return { text: `Active ${days}d ago`, tone: "text-emerald-600 bg-emerald-50" };
  if (days <= 14) return { text: `${days}d ago`, tone: "text-amber-600 bg-amber-50" };
  return { text: `Quiet · ${days}d`, tone: "text-gray-500 bg-gray-100" };
};

export default function Community() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [cat, setCat] = useState("All");
  const [tab, setTab] = useState("foryou");
  const [events, setEvents] = useState([]);
  const [trending, setTrending] = useState([]);
  const [ref, setRef] = useState(null);
  const [following, setFollowing] = useState([]);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [active, setActive] = useState(null);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [members, setMembers] = useState([]);
  const [mentionSel, setMentionSel] = useState({});
  const [mentionQ, setMentionQ] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", category: "Business Growth", type: "discussion", options: [{ id: crypto.randomUUID(), value: "" }, { id: crypto.randomUUID(), value: "" }] });
  const replyRef = useRef(null);
  const titleRef = useRef(null);
  useEffect(() => {
    if (active) setTimeout(() => replyRef.current?.focus(), 120);
  }, [active]);
  useEffect(() => { if (replyTo) replyRef.current?.focus(); }, [replyTo]);
  useEffect(() => {
    if (!active) return;
    const id = active.id;
    const t = setInterval(() => {
      api.get(`/community/posts/${id}/comments`).then((r) => {
        setActive((a) => (a && a.id === id ? { ...a, comments: r.data } : a));
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [active?.id]);
  useEffect(() => { api.get("/community/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (open) setTimeout(() => titleRef.current?.focus(), 120);
  }, [open]);

  const load = useCallback(() => api.get("/community/posts", { params: { sort: tab === "latest" ? "recent" : "foryou", filter: tab === "following" ? "following" : "all" } }).then((r) => setPosts(r.data)), [tab]);
  const loadStats = useCallback(() => api.get("/community/stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const pid = sp.get("post");
    if (pid && posts.length) {
      const found = posts.find((p) => p.id === pid);
      if (found) { setActive(found); setSp({}, { replace: true }); }
    }
  }, [posts, sp, setSp]);
  useEffect(() => { loadStats(); api.get("/community/events").then((r) => setEvents(r.data)).catch(() => {}); api.get("/community/referral").then((r) => setRef(r.data)).catch(() => {}); api.get("/community/trending").then((r) => setTrending(r.data)).catch(() => {}); }, [loadStats]);
  useEffect(() => { setFollowing(user?.following || []); }, [user]);
  const isFollowing = (a) => following.includes(a);
  const toggleFollow = async (a) => {
    if (!a || a === user?.name) return;
    const { data } = await api.post("/community/follow", { author: a });
    setFollowing((f) => (data.following ? [...new Set([...f, a])] : f.filter((x) => x !== a)));
    toast.success(data.following ? `Following ${a}` : `Unfollowed ${a}`);
  };
  const rsvp = async (e) => {
    const { data } = await api.post(`/community/events/${e.id}/rsvp`);
    setEvents((es) => es.map((x) => (x.id === e.id ? { ...x, i_rsvped: data.i_rsvped, rsvp_count: data.rsvp_count } : x)));
    toast.success(data.i_rsvped ? "You're in! 🎉" : "RSVP removed");
  };
  const refLink = ref ? `${window.location.origin}/signup?ref=${ref.code}` : "";
  const copyRef = () => { navigator.clipboard.writeText(refLink); toast.success("Invite link copied 🔗"); };

  const vote = async (p, dir = "up") => {
    const { data } = await api.post(`/community/posts/${p.id}/vote`, { dir });
    setPosts((ps) => ps.map((x) => (x.id === p.id ? { ...x, votes: data.votes, upvoted: data.upvoted, downvoted: data.downvoted } : x)));
    setActive((a) => (a && a.id === p.id ? { ...a, votes: data.votes, upvoted: data.upvoted, downvoted: data.downvoted } : a));
  };
  const votePoll = async (p, idx) => {
    const { data } = await api.post(`/community/posts/${p.id}/poll-vote`, { option: idx });
    setPosts((ps) => ps.map((x) => (x.id === p.id ? { ...x, poll_results: data } : x)));
  };
  const setOpt = (i, v) => setForm((f) => ({ ...f, options: f.options.map((o, idx) => (idx === i ? { ...o, value: v } : o)) }));
  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, { id: crypto.randomUUID(), value: "" }] }));
  const rmOpt = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
  const emptyForm = { title: "", body: "", category: "Business Growth", type: "discussion", options: [{ id: crypto.randomUUID(), value: "" }, { id: crypto.randomUUID(), value: "" }] };
  const openNew = (prefill = "") => { setForm({ ...emptyForm, title: prefill }); setOpen(true); };
  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const payload = { title: form.title, body: form.body, category: form.category, type: form.type };
    if (form.type === "poll") {
      const opts = form.options.map((o) => o.value.trim()).filter(Boolean);
      if (opts.length < 2) return toast.error("Add at least 2 poll options");
      payload.options = opts;
    }
    await api.post("/community/posts", payload); setOpen(false); setForm(emptyForm);
    setTab("latest"); loadStats(); toast.success(form.type === "poll" ? "Poll posted 📊" : "Posted to community 🎉");
  };
  const openPost = async (p) => {
    const { data } = await api.get(`/community/posts/${p.id}`); setActive(data);
    setPosts((ps) => ps.map((x) => (x.id === p.id ? { ...x, views: (x.views || 0) + 1 } : x)));
  };
  const reactComment = async (c, type) => {
    const { data } = await api.post(`/community/posts/${active.id}/comments/${c.id}/react`, { type });
    setActive((a) => (a ? { ...a, comments: (a.comments || []).map((x) => (x.id === c.id ? { ...x, reactions: data.reactions, my_reactions: data.my_reactions } : x)) } : a));
  };
  const setBest = async (c) => {
    const { data } = await api.post(`/community/posts/${active.id}/best`, { comment_id: c.id });
    setActive((a) => (a ? { ...a, best_comment_id: data.best_comment_id } : a));
    toast.success(data.best_comment_id ? "Marked as best answer ✓" : "Best answer removed");
  };
  const onComposerChange = (val) => {
    setComment(val);
    const m = val.match(/@([^\s@]*)$/);
    setMentionQ(m ? m[1].toLowerCase() : null);
  };
  const pickMention = (mem) => {
    setComment((prev) => prev.replace(/@([^\s@]*)$/, `@${mem.name} `));
    if (mem.id) setMentionSel((s) => ({ ...s, [mem.name]: mem.id }));
    setMentionQ(null);
    setTimeout(() => replyRef.current?.focus(), 30);
  };
  const addComment = async () => {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      const payload = { body: comment };
      if (replyTo) payload.parent_id = replyTo.id;
      const names = Object.keys(mentionSel).filter((n) => comment.includes("@" + n));
      if (names.length) { payload.mentions = names; payload.mention_ids = names.map((n) => mentionSel[n]).filter(Boolean); }
      const { data } = await api.post(`/community/posts/${active.id}/comments`, payload);
      setActive((a) => ({ ...a, comments: [...(a.comments || []), data] })); setComment(""); setReplyTo(null); setMentionSel({}); setMentionQ(null); load(); loadStats();
      toast.success(replyTo ? "Reply posted · +15 to your score" : "Comment posted · +15 to your score");
    } finally { setPosting(false); }
  };

  const filtered = cat === "All" ? posts : cat === "Polls" ? posts.filter((p) => p.is_poll) : posts.filter((p) => p.category === cat);
  const unanswered = posts.filter((p) => p.unanswered).slice(0, 4);
  const live = 42 + (posts.length * 7) % 120;
  const weekPosts = Math.max(8, posts.length * 2);

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="The MFD Community" subtitle="This is home. Ask, answer and grow with thousands of MFDs — your business tools are always one tap away." icon="MessageSquare" color="#A3E635"
        action={<Btn onClick={() => openNew()} data-testid="community-new-post-btn"><Icon name="PenLine" size={16} /> New Post</Btn>} />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5" data-testid="community-live-strip">
        <LivePulse label={`${live} MFDs online now`} />
        <SocialProof icon="MessagesSquare">{posts.length}+ active discussions</SocialProof>
        <SocialProof icon="Flame">{weekPosts} posts shared this week</SocialProof>
      </div>

      {trending.length > 0 && (
        <div className="mb-6" data-testid="community-trending">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Flame" size={16} className="text-rose-400" /><h3 className="font-head font-semibold text-sm">Trending this week</h3>
            <span className="text-[11px] text-gray-400">fastest-rising right now</span>
          </div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {trending.map((t, i) => (
              <button key={t.id} onClick={() => openPost({ id: t.id })} data-testid="trending-card"
                className="shrink-0 w-64 text-left card-mfx p-3 hover-lift">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-5 h-5 rounded-full bg-rose-500/15 text-rose-400 grid place-items-center text-[11px] font-bold">{i + 1}</span>
                  <span className="text-[10px] font-semibold bg-violet-50 text-violet-600 rounded-full px-2 py-0.5">{t.category}</span>
                  {t.is_poll && <HotBadge badge="In Demand" />}
                </div>
                <div className="text-sm font-semibold line-clamp-2">{t.title}</div>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                  <span className="flex items-center gap-1"><Icon name="ArrowBigUp" size={12} />{t.votes}</span>
                  <span className="flex items-center gap-1"><Icon name="MessageCircle" size={12} />{t.comments}</span>
                  <span className="flex items-center gap-1"><Icon name="Eye" size={12} />{t.views}</span>
                  <span className="ml-auto flex items-center gap-1 text-rose-400 font-semibold"><Icon name="TrendingUp" size={12} />{t.velocity}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Nudge banner */}
      <div className="rounded-[22px] bg-[#0A0A0A] text-white p-6 mb-6 relative overflow-hidden" data-testid="community-nudge">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full grad-blue-violet opacity-25 blur-3xl" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1"><Icon name="Flame" size={18} className="text-orange-400" /><span className="font-head font-semibold text-lg">Share something today</span></div>
            <p className="text-gray-400 text-sm">MFDs who post weekly get 3× more replies. Your voice helps the whole community grow.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {PROMPTS.map((p) => (
                <button key={p} onClick={() => openNew(p)} data-testid="community-prompt-chip"
                  className="text-left text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5 transition-colors">{p}</button>
              ))}
            </div>
          </div>
          <Btn onClick={() => openNew()} className="shrink-0" data-testid="community-nudge-btn"><Icon name="PenLine" size={16} /> Write a post</Btn>
        </div>
      </div>

      <div className="flex gap-2 mb-4" data-testid="feed-tabs">
        {[["foryou", "For You", "Sparkles"], ["latest", "Latest", "Clock"], ["following", "Following", "Users"]].map(([k, label, icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`feed-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${tab === k ? "grad-amber text-black border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>
            <Icon name={icon} size={15} /> {label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`community-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border ${cat === c ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{c}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          {filtered.map((p) => {
            const act = activityLabel(p.last_activity_days);
            return (
              <div key={p.id} className="card-mfx p-4 hover-lift flex gap-4" data-testid="community-post">
                <div className="flex flex-col items-center gap-0.5">
                  <button onClick={() => vote(p, "up")} data-testid="community-upvote-btn" className={`p-1 rounded-lg transition-colors ${p.upvoted ? "text-blue-600 bg-blue-50" : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"}`}><Icon name="ArrowBigUp" size={20} /></button>
                  <span className={`text-sm font-bold ${p.upvoted ? "text-blue-600" : p.downvoted ? "text-rose-600" : "text-gray-700"}`} data-testid="community-vote-count">{p.votes}</span>
                  <button onClick={() => vote(p, "down")} data-testid="community-downvote-btn" className={`p-1 rounded-lg transition-colors ${p.downvoted ? "text-rose-600 bg-rose-50" : "text-gray-400 hover:text-rose-600 hover:bg-rose-50"}`}><Icon name="ArrowBigDown" size={20} /></button>
                </div>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openPost(p)}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <img src={pfp(p.author, user)} alt={p.author} onClick={(e) => { e.stopPropagation(); setProfile(p.author); }} className="w-6 h-6 rounded-full object-cover cursor-pointer" loading="lazy" data-testid="community-author" />
                    <span className="text-[11px] font-semibold bg-violet-50 text-violet-600 rounded-full px-2 py-0.5">{p.category}</span>
                    {p.is_poll && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-blue-100 text-blue-600 flex items-center gap-1" data-testid="community-poll-badge"><Icon name="BarChart3" size={11} /> Poll</span>}
                    <HotBadge badge={p.is_poll ? "In Demand" : (p.last_activity_days != null && p.last_activity_days <= 2 ? "Trending" : ((p.views || 0) >= 500 ? "Popular" : hotFor(p.id).badge))} />
                    {!p.is_self && (
                      <button onClick={(e) => { e.stopPropagation(); toggleFollow(p.author); }} data-testid="community-follow-btn"
                        className={`text-[10px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${isFollowing(p.author) ? "bg-amber-500/15 text-amber-600 border-amber-300" : "bg-white border-gray-200 text-gray-500 hover:border-amber-300"}`}>
                        {isFollowing(p.author) ? "Following" : "+ Follow"}
                      </button>
                    )}
                    <span onClick={(e) => { e.stopPropagation(); setProfile(p.author); }} className="text-xs text-gray-400 hover:text-gray-700 cursor-pointer" data-testid="community-author-name">· {p.author}</span>
                    <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${act.tone}`} data-testid="community-activity">{act.text}</span>
                    {p.unanswered && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-orange-100 text-orange-600" data-testid="community-unanswered">Unanswered</span>}
                  </div>
                  <h3 className="font-head font-semibold">{p.title}</h3>
                  {p.is_poll ? (
                    <div className="mt-2" onClick={(e) => e.stopPropagation()} data-testid="community-poll">
                      <PollBlock p={p} onVote={votePoll} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.body}</p>
                  )}
                  {p.last_comment && (
                    <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-1.5 flex gap-1.5 items-start" data-testid="community-last-comment">
                      <Icon name="CornerDownRight" size={13} className="text-gray-400 shrink-0 mt-0.5" />
                      <span className="truncate"><b className="text-gray-700">{p.last_comment.author}:</b> {p.last_comment.body}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 flex-wrap">
                    <span className="flex items-center gap-1"><Icon name="MessageCircle" size={13} /> {p.comment_count} answers</span>
                    <span className="flex items-center gap-1" data-testid="community-views"><Icon name="Eye" size={13} /> {(p.views || 0).toLocaleString("en-IN")} views</span>
                    <span className="flex items-center gap-1 text-blue-600 font-semibold" data-testid="community-score"><Icon name="Zap" size={13} /> {p.score} pts</span>
                    <span className="flex items-center gap-1"><Icon name="Bookmark" size={13} /> Save</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-5">
          {/* Your community stats */}
          {stats && (
            <Card data-testid="community-my-stats">
              <div className="flex items-center gap-2 mb-3"><Icon name="Award" size={18} style={{ color: "#8B5CF6" }} /><h3 className="font-head font-semibold">Your community score</h3></div>
              <div className="flex items-end gap-2 mb-1">
                <span className="font-head font-bold text-3xl text-blue-600" data-testid="community-my-score">{stats.score}</span>
                <span className="text-xs font-semibold bg-violet-50 text-violet-600 rounded-full px-2 py-0.5 mb-1">{stats.level}</span>
              </div>
              {stats.next_level_at > stats.score && (
                <>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden mt-2"><div className="h-full rounded-full grad-blue-violet" style={{ width: `${Math.min(100, (stats.score / stats.next_level_at) * 100)}%` }} /></div>
                  <p className="text-[11px] text-gray-400 mt-1">{stats.next_level_at - stats.score} pts to next level</p>
                </>
              )}
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div><div className="font-head font-bold text-lg">{stats.posts}</div><div className="text-[11px] text-gray-400">Posts</div></div>
                <div><div className="font-head font-bold text-lg">{stats.answers}</div><div className="text-[11px] text-gray-400">Answers</div></div>
                <div><div className="font-head font-bold text-lg flex items-center justify-center gap-0.5">{stats.streak}<Icon name="Flame" size={14} className="text-orange-500" /></div><div className="text-[11px] text-gray-400">Week streak</div></div>
              </div>
              <Btn onClick={() => openNew()} className="mt-4 w-full justify-center" data-testid="community-stats-post-btn"><Icon name="PenLine" size={15} /> Earn +40 — write a post</Btn>
            </Card>
          )}

          {/* Community events */}
          {events.length > 0 && (
            <Card data-testid="community-events">
              <div className="flex items-center gap-2 mb-3"><Icon name="CalendarClock" size={18} style={{ color: "#F59E0B" }} /><h3 className="font-head font-semibold">Community events</h3></div>
              <div className="space-y-3">
                {events.map((e) => (
                  <div key={e.id} className="rounded-xl border border-gray-100 p-3" data-testid="community-event">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-violet-50 text-violet-600">{e.type}</span>
                      <Countdown to={e.starts_at} label="Starts in" />
                    </div>
                    <div className="text-sm font-semibold leading-snug">{e.title}</div>
                    <div className="text-[11px] text-gray-400 mt-0.5 flex items-center gap-1 flex-wrap"><Icon name="User" size={11} /> {e.host} · <Icon name="MapPin" size={11} /> {e.location}</div>
                    <div className="flex items-center justify-between mt-2">
                      <SocialProof icon="Users">{e.rsvp_count} going</SocialProof>
                      <button onClick={() => rsvp(e)} data-testid="community-rsvp-btn"
                        className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${e.i_rsvped ? "bg-emerald-500/15 text-emerald-600 border border-emerald-300" : "grad-amber text-black"}`}>
                        {e.i_rsvped ? "✓ Going" : "RSVP"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Invite peers */}
          {ref && (
            <Card data-testid="community-referral">
              <div className="flex items-center gap-2 mb-2"><Icon name="Gift" size={18} style={{ color: "#A3E635" }} /><h3 className="font-head font-semibold">Invite peers</h3></div>
              <p className="text-sm text-gray-500">Bring a fellow MFD into the community. You get <b className="text-amber-600">₹100</b> when they join.</p>
              <div className="flex items-center gap-2 mt-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                <Icon name="Link2" size={14} className="text-gray-400 shrink-0" />
                <span className="text-xs text-gray-600 truncate flex-1" data-testid="referral-link">{refLink}</span>
                <button onClick={copyRef} data-testid="referral-copy-btn" className="text-amber-600 hover:text-amber-500 shrink-0"><Icon name="Copy" size={15} /></button>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <a href={`https://wa.me/?text=${encodeURIComponent(`Join me on MFDX — the community for mutual fund distributors. ${refLink}`)}`} target="_blank" rel="noreferrer" data-testid="referral-whatsapp" className="flex-1 text-center text-xs font-semibold rounded-lg py-2 bg-emerald-500/15 text-emerald-600 border border-emerald-300">Share on WhatsApp</a>
                <span className="text-xs text-gray-400 flex items-center gap-1 px-1" data-testid="referral-invites"><Icon name="UserPlus" size={13} /> {ref.invites} joined</span>
              </div>
            </Card>
          )}

          {/* Help answer these */}
          {unanswered.length > 0 && (
            <Card data-testid="community-unanswered-list">
              <div className="flex items-center gap-2 mb-3"><Icon name="HelpCircle" size={18} style={{ color: "#F97316" }} /><h3 className="font-head font-semibold">Help answer these</h3></div>
              <div className="space-y-2">
                {unanswered.map((p) => (
                  <button key={p.id} onClick={() => openPost(p)} className="w-full text-left rounded-xl border border-gray-100 p-3 hover:border-orange-200 transition-colors" data-testid="community-unanswered-item">
                    <div className="text-sm font-medium line-clamp-1">{p.title}</div>
                    <div className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5"><Icon name="Eye" size={11} /> {(p.views || 0).toLocaleString("en-IN")} views · be the first to answer</div>
                  </button>
                ))}
              </div>
            </Card>
          )}

          <Card>
            <h3 className="font-head font-semibold flex items-center gap-2 mb-3"><Icon name="Trophy" size={18} style={{ color: "#F97316" }} /> Leaderboard</h3>
            <div className="space-y-3">
              {LEADERBOARD.map((l, i) => (
                <div key={l.n} className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-500"}`}>{i + 1}</span>
                  <img src={avatarFor(l.n)} alt={l.n} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{l.n}</div><div className="text-[11px] text-gray-400">{l.badge}</div></div>
                  <span className="ml-auto text-xs font-bold text-blue-600">{l.pts.toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">Ranked by contribution & learning — never by AUM or returns.</p>
          </Card>
          <Card>
            <h3 className="font-head font-semibold mb-2">Trust & moderation</h3>
            <p className="text-sm text-gray-500">We clearly separate <b className="text-blue-600">Verified info</b>, <b className="text-gray-700">community opinion</b> and <b className="text-violet-600">AI content</b>. Report, mute or block anytime.</p>
          </Card>
        </div>
      </div>

      {profile && (
        <Modal onClose={() => setProfile(null)} title="Member profile">
          <div className="flex items-center gap-3 mb-4">
            <img src={avatarFor(profile)} alt={profile} className="w-14 h-14 rounded-full object-cover" />
            <div>
              <div className="flex items-center gap-1 font-head font-semibold text-lg">{profile}<Icon name="BadgeCheck" size={16} className="text-blue-500" /></div>
              <div className="text-xs text-gray-400">Verified MFD · MFDX Community</div>
            </div>
            {profile !== user?.name && (
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => toggleFollow(profile)} data-testid="profile-follow-btn"
                  className={`text-sm font-semibold rounded-xl px-4 py-2 ${isFollowing(profile) ? "bg-amber-500/15 text-amber-600 border border-amber-300" : "grad-amber text-black"}`}>
                  {isFollowing(profile) ? "Following" : "+ Follow"}
                </button>
                {isFollowing(profile) && (
                  <button onClick={() => nav("/app/messages", { state: { startName: profile } })} data-testid="profile-message-btn"
                    className="text-sm font-semibold rounded-xl px-4 py-2 bg-[#1E2435] text-slate-100 border border-[#2E384D]">
                    <Icon name="Send" size={14} className="inline mr-1" />Message
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {["Community Expert", "Digital MFD", "Top Contributor"].map((b) => <span key={b} className="text-[11px] font-semibold bg-violet-50 text-violet-600 rounded-full px-2.5 py-1">{b}</span>)}
          </div>
          <div className="text-xs font-bold text-gray-500 uppercase mb-2">Posts by {profile.split(" ")[0]}</div>
          <div className="space-y-2">
            {posts.filter((x) => x.author === profile).map((x) => (
              <div key={x.id} className="rounded-xl border border-gray-100 p-3"><div className="text-sm font-medium">{x.title}</div><div className="text-[11px] text-gray-400">{x.votes} upvotes · {x.comment_count} answers · {(x.views || 0).toLocaleString("en-IN")} views</div></div>
            ))}
            {posts.filter((x) => x.author === profile).length === 0 && <p className="text-sm text-gray-400">No posts yet.</p>}
          </div>
        </Modal>
      )}
      {open && (
        <Modal onClose={() => setOpen(false)} title="New Community Post">
          <div className="flex gap-2 mb-3" data-testid="post-type-toggle">
            {[["discussion", "Discussion", "MessageSquare"], ["poll", "Poll", "BarChart3"]].map(([t, label, icon]) => (
              <button key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`post-type-${t}`}
                className={`flex-1 flex items-center justify-center gap-1.5 rounded-xl border py-2 text-sm font-medium ${form.type === t ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>
                <Icon name={icon} size={15} /> {label}
              </button>
            ))}
          </div>
          <input ref={titleRef} placeholder={form.type === "poll" ? "Ask a poll question" : "Your question or title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="community-post-title" className={inp} />
          {form.type === "poll" ? (
            <div className="mt-3 space-y-2" data-testid="poll-options">
              {form.options.map((o, i) => (
                <div key={o.id} className="flex gap-2 items-center">
                  <input value={o.value} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`poll-option-input-${i}`} className={inp} />
                  {form.options.length > 2 && <button onClick={() => rmOpt(i)} className="text-gray-400 hover:text-red-500 shrink-0"><Icon name="X" size={16} /></button>}
                </div>
              ))}
              {form.options.length < 5 && <button onClick={addOpt} data-testid="poll-add-option" className="text-sm text-blue-600 font-medium flex items-center gap-1"><Icon name="Plus" size={14} /> Add option</button>}
            </div>
          ) : (
            <textarea placeholder="Add details…" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="community-post-body" className={`${inp} mt-3`} />
          )}
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={`${inp} mt-3`}>{CAT_OPTIONS.map((c) => <option key={c}>{c}</option>)}</select>
          <Btn onClick={create} className="mt-4 w-full justify-center" data-testid="community-submit-post">{form.type === "poll" ? "Create Poll" : "Post"}</Btn>
        </Modal>
      )}

      {active && (
        <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm grid place-items-center p-3 sm:p-4" onClick={(e) => { if (e.target === e.currentTarget) setActive(null); }} data-testid="community-thread-modal">
          <div className="w-full max-w-xl card-mfx flex flex-col max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100">
              <img src={pfp(active.author, user)} alt={active.author} onClick={() => setProfile(active.author)} className="w-10 h-10 rounded-full object-cover cursor-pointer" data-testid="thread-author-avatar" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 font-semibold text-sm text-gray-900">{active.author}<Icon name="BadgeCheck" size={14} className="text-blue-500" /></div>
                <div className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="bg-violet-50 text-violet-600 rounded-full px-2 py-0.5 font-semibold">{active.category}</span>
                  <span className="flex items-center gap-1"><Icon name="Eye" size={11} /> {(active.views || 0).toLocaleString("en-IN")} views</span>
                </div>
              </div>
              <button onClick={() => setActive(null)} className="text-gray-400 hover:text-gray-700 shrink-0" data-testid="community-thread-close"><Icon name="X" size={20} /></button>
            </div>

            {/* Scrollable post + comments */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <h3 className="font-head font-bold text-lg text-gray-900 mb-2 leading-snug">{active.title}</h3>
              {active.body && <p className="text-gray-600 text-sm whitespace-pre-wrap">{active.body}</p>}
              {active.is_poll && <div className="mt-3"><PollBlock p={active} onVote={votePoll} /></div>}

              <div className="flex items-center gap-3 mt-4 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-1 rounded-full border border-gray-200 px-1 py-0.5" data-testid="community-modal-vote">
                  <button onClick={() => vote(active, "up")} className={`p-1 rounded-full transition-colors ${active.upvoted ? "text-blue-600 bg-blue-50" : "text-gray-400 hover:text-blue-600"}`} data-testid="modal-upvote-btn"><Icon name="ArrowBigUp" size={18} /></button>
                  <span className={`text-sm font-bold min-w-[1.5rem] text-center ${active.upvoted ? "text-blue-600" : active.downvoted ? "text-rose-600" : "text-gray-700"}`}>{active.votes ?? 0}</span>
                  <button onClick={() => vote(active, "down")} className={`p-1 rounded-full transition-colors ${active.downvoted ? "text-rose-600 bg-rose-50" : "text-gray-400 hover:text-rose-600"}`} data-testid="modal-downvote-btn"><Icon name="ArrowBigDown" size={18} /></button>
                </div>
                <span className="text-xs text-gray-500 font-medium flex items-center gap-1"><Icon name="MessageCircle" size={14} /> {active.comments?.length || 0} {(active.comments?.length || 0) === 1 ? "comment" : "comments"}</span>
              </div>

              <div className="mt-4 space-y-4" data-testid="community-comments">
                {[...(active.comments || []).filter((c) => !c.parent_id)]
                  .sort((a, b) => ((b.id === active.best_comment_id) - (a.id === active.best_comment_id)))
                  .map((c) => (
                  <CommentItem key={c.id} c={c} replies={(active.comments || []).filter((r) => r.parent_id === c.id)}
                    onReact={reactComment} onReply={setReplyTo} onProfile={setProfile} me={user?.name} meUser={user}
                    isBest={c.id === active.best_comment_id} canBest={active.author_id === user?.id} onBest={setBest} />
                ))}
                {(!active.comments || active.comments.length === 0) && (
                  <div className="text-center py-6">
                    <Icon name="MessageSquare" size={28} className="text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No comments yet — be the first to reply <span className="text-amber-600 font-medium">(+15 points)</span>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky inline composer */}
            <div className="border-t border-gray-100 bg-white relative">
              {mentionQ !== null && members.filter((m) => m.name.toLowerCase().includes(mentionQ)).length > 0 && (
                <div className="absolute bottom-full left-3 right-3 mb-1 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl z-10" data-testid="mention-dropdown">
                  {members.filter((m) => m.name.toLowerCase().includes(mentionQ)).slice(0, 6).map((m) => (
                    <button key={m.name} onClick={() => pickMention(m)} data-testid="mention-option" className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left">
                      <img src={avatarFor(m.name)} alt={m.name} className="w-6 h-6 rounded-full object-cover" />
                      <span className="text-sm text-gray-700">{m.name}</span>
                    </button>
                  ))}
                </div>
              )}
              {replyTo && (
                <div className="flex items-center justify-between px-4 pt-2 text-xs text-gray-500" data-testid="reply-context">
                  <span className="truncate flex items-center gap-1"><Icon name="CornerDownRight" size={13} className="text-blue-500" /> Replying to <b className="text-gray-700">{replyTo.author}</b></span>
                  <button onClick={() => setReplyTo(null)} data-testid="reply-cancel-btn" className="text-gray-400 hover:text-gray-700"><Icon name="X" size={14} /></button>
                </div>
              )}
              <div className="px-3 py-2.5 flex items-end gap-2">
                <img src={userAvatar(user)} alt="you" className="w-8 h-8 rounded-full object-cover shrink-0 mb-0.5" />
                <textarea ref={replyRef} rows={1} placeholder={replyTo ? `Reply to ${replyTo.author}…` : "Write a comment… use @ to mention"} value={comment}
                  onChange={(e) => onComposerChange(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && mentionQ === null) { e.preventDefault(); addComment(); } }}
                  data-testid="community-comment-input"
                  className="flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm outline-none focus:border-blue-400 focus:bg-white max-h-28 leading-snug" />
                <button onClick={addComment} disabled={!comment.trim() || posting} data-testid="community-comment-btn"
                  className="shrink-0 w-9 h-9 grid place-items-center rounded-full grad-amber text-black transition-opacity disabled:opacity-40 hover:brightness-105">
                  <Icon name="Send" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LiveNudge id="community" title="Join the conversation" message="MFDs who post weekly get 3× more replies. Share a win or a question today." ctaLabel="Write a post" onCta={() => openNew()} />
    </div>
  );
}

const RX = [
  { k: "like", icon: "Heart", cls: "text-rose-600", hov: "hover:text-rose-600", fill: "fill-rose-600" },
  { k: "up", icon: "ThumbsUp", cls: "text-blue-600", hov: "hover:text-blue-600", fill: "fill-blue-600" },
  { k: "idea", icon: "Lightbulb", cls: "text-amber-600", hov: "hover:text-amber-600", fill: "fill-amber-500" },
  { k: "fire", icon: "Flame", cls: "text-orange-600", hov: "hover:text-orange-600", fill: "fill-orange-500" },
];

function renderBody(body, mentions) {
  if (!body || !mentions || !mentions.length) return body;
  const esc = [...mentions].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp("@(" + esc.join("|") + ")", "g");
  const parts = []; let last = 0; let m; let i = 0;
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    parts.push(<span key={`m${i++}`} className="text-blue-600 font-semibold">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}

function ReactionBar({ c, onReact }) {
  return (
    <div className="flex items-center gap-3">
      {RX.map((r) => {
        const count = c.reactions?.[r.k] || 0;
        const mine = (c.my_reactions || []).includes(r.k);
        return (
          <button key={r.k} onClick={() => onReact(c, r.k)} data-testid={`comment-react-${r.k}`}
            className={`text-xs font-semibold flex items-center gap-1 transition-colors ${mine ? r.cls : "text-gray-400 " + r.hov}`}>
            <Icon name={r.icon} size={13} className={mine ? r.fill : ""} />{count > 0 ? count : ""}
          </button>
        );
      })}
    </div>
  );
}

function ReplyItem({ c, onReact, onProfile, me, meUser }) {
  return (
    <div className="flex gap-2" data-testid="community-reply">
      <img src={pfp(c.author, meUser)} alt={c.author} onClick={() => onProfile(c.author)} className="w-7 h-7 rounded-full object-cover shrink-0 cursor-pointer" />
      <div className="min-w-0 flex-1">
        <div className="bg-gray-50 rounded-2xl rounded-tl-sm px-3.5 py-2">
          <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">{c.author}{c.author === me && <span className="text-[9px] font-bold text-amber-600 bg-amber-100 rounded-full px-1.5">You</span>}</div>
          <p className="text-sm text-gray-600 whitespace-pre-wrap mt-0.5">{renderBody(c.body, c.mentions)}</p>
        </div>
        <div className="mt-1 pl-1"><ReactionBar c={c} onReact={onReact} /></div>
      </div>
    </div>
  );
}

function CommentItem({ c, replies, onReact, onReply, onProfile, me, meUser, isBest, canBest, onBest }) {
  return (
    <div className="flex gap-2.5" data-testid="community-comment">
      <img src={pfp(c.author, meUser)} alt={c.author} onClick={() => onProfile(c.author)} className="w-8 h-8 rounded-full object-cover shrink-0 cursor-pointer" />
      <div className="min-w-0 flex-1">
        {isBest && <div className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 mb-1" data-testid="best-answer-badge"><Icon name="CheckCircle2" size={11} /> Best Answer</div>}
        <div className={`rounded-2xl rounded-tl-sm px-3.5 py-2 ${isBest ? "bg-emerald-50 ring-1 ring-emerald-200" : "bg-gray-50"}`}>
          <div className="text-xs font-semibold text-gray-800 flex items-center gap-1">{c.author}{c.author === me && <span className="text-[9px] font-bold text-amber-600 bg-amber-100 rounded-full px-1.5">You</span>}</div>
          <p className="text-sm text-gray-600 whitespace-pre-wrap mt-0.5">{renderBody(c.body, c.mentions)}</p>
        </div>
        <div className="flex items-center gap-4 mt-1 pl-1 flex-wrap">
          <ReactionBar c={c} onReact={onReact} />
          <button onClick={() => onReply(c)} data-testid="comment-reply-btn" className="text-xs font-semibold text-gray-400 hover:text-blue-600">Reply</button>
          {canBest && <button onClick={() => onBest(c)} data-testid="comment-best-btn" className={`text-xs font-semibold ${isBest ? "text-emerald-600" : "text-gray-400 hover:text-emerald-600"}`}>{isBest ? "Unmark best" : "Mark best"}</button>}
        </div>
        {replies && replies.length > 0 && (
          <div className="mt-2.5 space-y-2.5 border-l-2 border-gray-100 pl-2.5">
            {replies.map((r) => <ReplyItem key={r.id} c={r} onReact={onReact} onProfile={onProfile} me={me} meUser={meUser} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PollBlock({ p, onVote }) {
  const r = p.poll_results || { options: [], total: 0, my_choice: null };
  const voted = r.my_choice != null;
  return (
    <div className="space-y-1.5" data-testid="poll-block">
      {r.options.map((o, i) => {
        const chosen = r.my_choice === i;
        return (
          <button key={o.text} onClick={() => onVote(p, i)} data-testid={`poll-option-${i}`}
            className={`relative w-full text-left rounded-lg border px-3 py-2 overflow-hidden transition-colors ${chosen ? "border-blue-400" : "border-gray-200 hover:border-blue-300"}`}>
            <div className="absolute inset-y-0 left-0 rounded-lg" style={{ width: `${o.pct}%`, background: chosen ? "#dbeafe" : "#f1f5f9" }} />
            <div className="relative flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5">{chosen && <Icon name="Check" size={13} className="text-blue-600" />}{o.text}</span>
              <span className="text-gray-500 font-medium tabular-nums" data-testid={`poll-pct-${i}`}>{o.pct}% · {o.votes}</span>
            </div>
          </button>
        );
      })}
      <div className="text-[11px] text-gray-400 pt-0.5" data-testid="poll-total">{r.total} votes · {voted ? "you voted · tap to change" : "tap an option to vote"}</div>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg card-mfx p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-head font-bold text-lg pr-6">{title}</h3><button onClick={onClose} className="text-gray-400 hover:text-gray-700"><Icon name="X" size={20} /></button></div>
        {children}
      </div>
    </div>
  );
}
const inp = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400";
